import { Link, useNavigate } from '@tanstack/react-router';
import { Badge } from '@tempo-io/sui-badge';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgName } from '@/common/hooks/useOrgName';
import { useFindingRefsLookup } from '../data/FindingRefsContext';
import type { ReferenceKind } from '../data/types';

interface ReferenceChipProps {
  kind: ReferenceKind;
  id: number;
  /**
   * True inside the finding card's accordion trigger button — a real `<a>` can't legally nest
   * inside a `<button>`, so an objective reference here can't use `Link`. See `ObjectiveButton`.
   */
  inert?: boolean;
}

// Name first, key in parentheses: the name is what a reader recognises, the key is what they cite
// when handing the item to someone else.
const itemLabel = (item: { externalKey?: string; name?: string } | undefined): string | null => {
  if (!item) return null;
  if (item.name && item.externalKey) return `${item.name} (${item.externalKey})`;
  return item.name ?? item.externalKey ?? null;
};

/**
 * The `inert` objective chip, made clickable without nesting a real `<a>` inside the accordion
 * trigger's `<button>`: stops the click before it reaches the trigger (which would otherwise just
 * toggle the card) and navigates manually instead of relying on `Link`. Same pattern as a star
 * icon inside a clickable list row — `role="button"` + manual key handling keeps it keyboard
 * accessible without being real interactive content nested in another control.
 */
const ObjectiveButton = ({ label, org }: { label: string; org: string }) => {
  const navigate = useNavigate();
  const go = () => navigate({ to: '/org/$org/objectives', params: { org } });

  return (
    <Badge
      variant="default"
      role="button"
      tabIndex={0}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        go();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        go();
      }}
      className="align-middle cursor-pointer whitespace-nowrap hover:underline"
    >
      {label}
    </Badge>
  );
};

/**
 * A single resolved `[item:id]` / `[objective:id]` reference. Items render a label only in this
 * PR — clicking through to the item panel is a follow-up once the route can accept its search
 * params. Objectives already link to the objectives list: there is no per-objective page to deep
 * link to, so every objective reference points at the same destination regardless of which one it
 * resolved to.
 */
export const ReferenceChip = ({ kind, id, inert = false }: ReferenceChipProps) => {
  const { t } = useTranslation();
  const refs = useFindingRefsLookup();
  const org = useOrgName();

  if (kind === 'item') {
    const label = itemLabel(refs.item(id)) ?? t(($) => $.loopAgenticInsights.reference.item);
    return (
      <Badge variant="default" className="align-middle whitespace-nowrap">
        {label}
      </Badge>
    );
  }

  const label = refs.objective(id) ?? t(($) => $.loopAgenticInsights.reference.objective);

  if (inert) {
    return <ObjectiveButton label={label} org={org} />;
  }

  return (
    <Badge asChild variant="default" className="align-middle whitespace-nowrap">
      <Link to="/org/$org/objectives" params={{ org }}>
        {label}
      </Link>
    </Badge>
  );
};
